<template>
  <div class="whitelist-page">
    <PageHeader
      title="IP Whitelist"
      subtitle="Restrict ScrapingAnt access to a known set of IPs."
    >
      <template #actions>
        <v-btn color="primary" depressed @click="openAddDialog(currentTabType)">
          <v-icon left small>mdi-plus</v-icon>Add IP
        </v-btn>
      </template>
    </PageHeader>

    <SectionCard flush>
      <v-tabs
        v-model="activeTab"
        class="whitelist-page__tabs"
        color="primary"
      >
        <v-tab class="whitelist-page__tab">Auth IP whitelist</v-tab>
        <v-tab class="whitelist-page__tab">API key IP whitelist</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="whitelist-page__items">
        <v-tab-item>
          <v-data-table
            v-if="authEntries.length > 0"
            :headers="headers"
            :items="authEntries"
            :items-per-page="10"
            :mobile-breakpoint="600"
            class="whitelist-page__table"
          >
            <template v-slot:item.ip_display="{ item }">
              <code class="whitelist-page__ip">{{ formatIpDisplay(item) }}</code>
            </template>
            <template v-slot:item.created_at="{ item }">
              <span class="whitelist-page__date">{{
                formatDate(item.created_at)
              }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="error"
                :aria-label="`Delete ${formatIpDisplay(item)}`"
                @click="openDeleteDialog('auth', item)"
              >
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <EmptyState
            v-else
            icon="mdi-ip-network-outline"
            title="No IPs whitelisted"
            description="Add your office or server IPs to lock dashboard login to known networks."
            action-label="Add your first IP"
            @action="openAddDialog('auth')"
          />
        </v-tab-item>

        <v-tab-item>
          <v-data-table
            v-if="apiKeyEntries.length > 0"
            :headers="headers"
            :items="apiKeyEntries"
            :items-per-page="10"
            :mobile-breakpoint="600"
            class="whitelist-page__table"
          >
            <template v-slot:item.ip_display="{ item }">
              <code class="whitelist-page__ip">{{ formatIpDisplay(item) }}</code>
            </template>
            <template v-slot:item.created_at="{ item }">
              <span class="whitelist-page__date">{{
                formatDate(item.created_at)
              }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="error"
                :aria-label="`Delete ${formatIpDisplay(item)}`"
                @click="openDeleteDialog('apiKey', item)"
              >
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <EmptyState
            v-else
            icon="mdi-key-outline"
            title="No API key restrictions"
            description="Add IPs that are allowed to call the API with your key. All other IPs will be rejected."
            action-label="Add your first IP"
            @action="openAddDialog('apiKey')"
          />
        </v-tab-item>
      </v-tabs-items>
    </SectionCard>

    <ConfirmDialog
      :value="addDialogActive"
      title="Add IP address"
      confirm-label="Add"
      cancel-label="Cancel"
      :loading="addingIp"
      :max-width="480"
      @confirm="addIp"
      @input="addDialogActive = $event"
    >
      <v-text-field
        v-model="newIp.ip_address"
        label="IP address"
        placeholder="e.g. 203.0.113.42"
        :error-messages="ipAddressErrors"
        outlined
        dense
        autofocus
        @input="validateIpAddress"
      ></v-text-field>
      <v-text-field
        v-model="newIp.mask"
        label="CIDR mask (optional)"
        placeholder="e.g. 24"
        type="number"
        min="0"
        max="32"
        :error-messages="maskErrors"
        outlined
        dense
        hint="Leave empty for a single IP, or enter 0–32 for a subnet"
        persistent-hint
        @input="validateMask"
      ></v-text-field>
    </ConfirmDialog>

    <ConfirmDialog
      :value="deleteDialogActive"
      title="Delete IP address?"
      confirm-label="Delete"
      cancel-label="Keep"
      confirm-color="error"
      :max-width="480"
      @confirm="deleteIp"
      @input="deleteDialogActive = $event"
    >
      <p>
        Are you sure you want to remove
        <strong>{{ itemToDelete ? formatIpDisplay(itemToDelete) : "" }}</strong>
        from the whitelist?
      </p>
    </ConfirmDialog>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const IPV4_REGEX =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const IPV6_REGEX =
  /^(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}$|^::(?:[a-fA-F0-9]{1,4}:){0,6}[a-fA-F0-9]{1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,7}:$|^(?:[a-fA-F0-9]{1,4}:){1,6}:[a-fA-F0-9]{1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,5}(?::[a-fA-F0-9]{1,4}){1,2}$|^(?:[a-fA-F0-9]{1,4}:){1,4}(?::[a-fA-F0-9]{1,4}){1,3}$|^(?:[a-fA-F0-9]{1,4}:){1,3}(?::[a-fA-F0-9]{1,4}){1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,2}(?::[a-fA-F0-9]{1,4}){1,5}$|^[a-fA-F0-9]{1,4}:(?::[a-fA-F0-9]{1,4}){1,6}$/;

export default {
  name: "IPWhitelist",
  components: { PageHeader, SectionCard, EmptyState, ConfirmDialog },
  data() {
    return {
      activeTab: 0,
      loading: false,
      addingIp: false,
      authEntries: [],
      apiKeyEntries: [],
      headers: [
        { text: "IP address", value: "ip_display", sortable: false },
        { text: "Created", value: "created_at" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      addDialogActive: false,
      addDialogType: null,
      newIp: {
        ip_address: "",
        mask: "",
      },
      ipAddressErrors: [],
      maskErrors: [],
      deleteDialogActive: false,
      deleteDialogType: null,
      itemToDelete: null,
    };
  },
  computed: {
    isAddDisabled() {
      return (
        !this.newIp.ip_address ||
        this.ipAddressErrors.length > 0 ||
        this.maskErrors.length > 0
      );
    },
    currentTabType() {
      return this.activeTab === 0 ? "auth" : "apiKey";
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [authData, apiKeyData] = await Promise.all([
          this.$store.dispatch("whitelist/getAuthWhitelist"),
          this.$store.dispatch("whitelist/getApiKeyWhitelist"),
        ]);
        this.authEntries = authData.entries || [];
        this.apiKeyEntries = apiKeyData.entries || [];
      } catch (error) {
        console.log(error);
        this.notifyError("Failed to load whitelist data");
      } finally {
        this.loading = false;
      }
    },
    formatIpDisplay(item) {
      if (item.mask !== null && item.mask !== undefined) {
        return `${item.ip_address}/${item.mask}`;
      }
      return item.ip_address;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    validateIpAddress() {
      this.ipAddressErrors = [];
      if (!this.newIp.ip_address) return;
      if (
        !IPV4_REGEX.test(this.newIp.ip_address) &&
        !IPV6_REGEX.test(this.newIp.ip_address)
      ) {
        this.ipAddressErrors.push("Please enter a valid IPv4 or IPv6 address");
      }
    },
    validateMask() {
      this.maskErrors = [];
      if (this.newIp.mask === "" || this.newIp.mask === null) return;
      const mask = parseInt(this.newIp.mask, 10);
      if (isNaN(mask) || mask < 0 || mask > 32) {
        this.maskErrors.push("Mask must be between 0 and 32");
      }
    },
    openAddDialog(type) {
      this.addDialogType = type;
      this.newIp = { ip_address: "", mask: "" };
      this.ipAddressErrors = [];
      this.maskErrors = [];
      this.addDialogActive = true;
    },
    async addIp() {
      this.validateIpAddress();
      this.validateMask();
      if (this.isAddDisabled) {
        return;
      }

      this.addingIp = true;
      try {
        const actionName =
          this.addDialogType === "auth"
            ? "whitelist/addAuthWhitelist"
            : "whitelist/addApiKeyWhitelist";

        await this.$store.dispatch(actionName, {
          ip_address: this.newIp.ip_address,
          mask: this.newIp.mask || null,
        });

        this.$notify({
          group: "main",
          classes: "main-notification success",
          text: "IP address added",
          duration: 2000,
        });

        this.addDialogActive = false;
        await this.loadData();
      } catch (error) {
        console.log(error);
        this.notifyError("Failed to add IP address");
      } finally {
        this.addingIp = false;
      }
    },
    openDeleteDialog(type, item) {
      this.deleteDialogType = type;
      this.itemToDelete = item;
      this.deleteDialogActive = true;
    },
    async deleteIp() {
      if (!this.itemToDelete) return;
      this.loading = true;
      this.deleteDialogActive = false;
      try {
        const actionName =
          this.deleteDialogType === "auth"
            ? "whitelist/deleteAuthWhitelist"
            : "whitelist/deleteApiKeyWhitelist";

        await this.$store.dispatch(actionName, this.itemToDelete.id);

        this.$notify({
          group: "main",
          classes: "main-notification success",
          text: "IP address removed",
          duration: 2000,
        });

        await this.loadData();
      } catch (error) {
        console.log(error);
        this.notifyError("Failed to delete IP address");
      } finally {
        this.loading = false;
        this.itemToDelete = null;
        this.deleteDialogType = null;
      }
    },
    notifyError(message) {
      this.$notify({
        group: "main",
        classes: "main-notification error",
        text: message,
        duration: 3500,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.whitelist-page {
  position: relative;

  &__tabs {
    ::v-deep .v-tabs-bar {
      background: transparent !important;
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    }

    ::v-deep .v-tab {
      text-transform: none;
      letter-spacing: 0;
      font-weight: 500;
      font-size: 13px;
      color: rgba(15, 23, 42, 0.6) !important;

      &--active {
        color: #0f172a !important;
        font-weight: 600;
      }
    }
  }

  &__items {
    padding: 24px;
  }

  &__table {
    background: transparent !important;

    ::v-deep {
      thead.v-data-table-header th {
        background-color: #f8f9fb !important;
        color: rgba(15, 23, 42, 0.6) !important;
        font-size: 11px !important;
        font-weight: 600 !important;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 12px 18px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
      }

      tbody td {
        font-size: 13px;
        color: #0f172a;
        padding: 10px 18px !important;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
      }

      tbody tr:hover {
        background-color: rgba(15, 23, 42, 0.02) !important;
      }
    }
  }

  &__ip {
    background-color: #f8f8fb;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 6px;
    padding: 4px 10px;
    font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    color: #0f172a;
  }

  &__date {
    color: rgba(15, 23, 42, 0.62);
  }
}
</style>
